import { useState } from 'react';
import { useConnectors } from '@/lib/use-connectors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/ui/section-header';
import {
  Plus,
  Loader2,
  Trash2,
  Plug,
  AlertCircle,
  CheckCircle2,
  Clock,
  Wrench,
  Edit
} from 'lucide-react';
import type { Connector } from '@/lib/connector-types';
import { ConnectorToolsDialog } from './ConnectorToolsDialog';
import { ConnectorConfigForm } from './ConnectorConfigForm';
import { ConnectorDeletionDialog } from './ConnectorDeletionDialog';

const statusConfig = {
  active: { icon: CheckCircle2, label: 'Connected', variant: 'success' as const },
  error: { icon: AlertCircle, label: 'Error', variant: 'destructive' as const },
  pending_auth: { icon: Clock, label: 'Pending Auth', variant: 'warning' as const },
  pending: { icon: Clock, label: 'Pending', variant: 'warning' as const },
  connected: { icon: CheckCircle2, label: 'Connected', variant: 'success' as const },
  refresh_error: { icon: AlertCircle, label: 'Refresh Error', variant: 'destructive' as const },
  reconnection_needed: { icon: AlertCircle, label: 'Reconnect Needed', variant: 'destructive' as const },
};

const typeLabels: Record<string, string> = {
  mcp: 'MCP'
};

export function ConnectorList({ workspaceId }: { workspaceId: string | null }) {
  const { connectors, loading, error, refresh, remove } = useConnectors();
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editTarget, setEditTarget] = useState<Connector | null>(null);
  const [toolsDialog, setToolsDialog] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Connector | null>(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await remove(deleteTarget.id);
    } catch (err: any) {
      alert(`Failed to delete connector: ${err.message}`);
    } finally {
      setDeleting(null);
      setDeleteTarget(null);
    }
  };

  if (view === 'create') {
    return (
      <ConnectorConfigForm
        workspaceId={workspaceId}
        onBack={() => setView('list')}
        onCreated={() => { setView('list'); refresh(); }}
      />
    );
  }

  if (view === 'edit' && editTarget) {
    return (
      <ConnectorConfigForm
        connector={editTarget}
        workspaceId={workspaceId}
        onBack={() => { setView('list'); setEditTarget(null); }}
        onCreated={() => { setView('list'); setEditTarget(null); refresh(); }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <SectionHeader
        icon={<Plug className="size-4 text-primary" />}
        title="Connectors"
        subtitle="Connect external services"
        footer={
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {connectors.length} connector{connectors.length !== 1 ? 's' : ''}
            </p>
            <Button size="sm" onClick={() => setView('create')} className="h-7 px-2 text-xs">
              <Plus className="size-3.5" />
              <span className="hidden min-[340px]:inline ml-1">New</span>
            </Button>
          </div>
        }
      />

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="size-6 text-primary animate-spin mb-3" />
            <p className="text-sm text-muted-foreground">Loading connectors...</p>
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
            <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        ) : connectors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="flex items-center justify-center size-14 rounded-2xl bg-muted mb-4">
              <Plug className="size-7 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium mb-1">No connectors yet</p>
            <p className="text-xs text-muted-foreground mb-5 text-center max-w-[240px]">
              Create a connector to integrate external services.
            </p>
            <Button size="sm" onClick={() => setView('create')}>
              <Plus className="size-3.5 mr-1.5" />
              New Connector
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {connectors.map(connector => (
              <ConnectorCard
                key={connector.id}
                connector={connector}
                onEdit={() => { setEditTarget(connector); setView('edit'); }}
                onDelete={() => setDeleteTarget(connector)}
                onViewTools={() => setToolsDialog(connector.id)}
                deleting={deleting === connector.id}
              />
            ))}
          </div>
        )}
      </div>

      {toolsDialog && (
        <ConnectorToolsDialog
          connectorId={toolsDialog}
          onClose={() => setToolsDialog(null)}
        />
      )}

      {deleteTarget && (
        <ConnectorDeletionDialog
          connector={deleteTarget}
          onConfirmDelete={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function ConnectorCard({
  connector,
  onEdit,
  onDelete,
  onViewTools,
  deleting,
}: {
  connector: Connector;
  onEdit: () => void;
  onDelete: () => void;
  onViewTools: () => void;
  deleting: boolean;
}) {
  const status = statusConfig[connector.status] || statusConfig.error;
  const StatusIcon = status.icon;

  return (
    <div className="group border rounded-lg bg-card p-3.5 transition-colors hover:bg-accent/40">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center size-8 rounded-md bg-muted shrink-0">
          <Plug className="size-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col items-start justify-center gap-1">
            <span className="text-sm font-medium truncate">{connector.name}</span>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <StatusIcon className="size-3" />
              <span>{status.label}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 w-7 p-0" title="Edit connector">
            <Edit className="size-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onViewTools} className="h-7 w-7 p-0" title="View tools">
            <Wrench className="size-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} disabled={deleting} className="h-7 w-7 p-0 text-destructive hover:text-destructive" title="Delete connector">
            {deleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
